import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { useSelector } from 'react-redux';

import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import { ResizableHandle, ResizablePanel } from '@/components/ui/resizable';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { selectSidebarPanelConfig } from '@/redux/uiConfig/selectors';

const PanelCloseContext = createContext<(() => void) | null>(null);

/**
 * Close the surrounding `<ResponsiveSidebarPanel>`. On mobile this flips the
 * local Vaul state so the exit animation plays before the parent unmounts;
 * on desktop it calls `onClose` synchronously. Throws if used outside a panel.
 */
export const useResponsiveSidebarClose = () => {
  const ctx = useContext(PanelCloseContext);
  if (!ctx) {
    throw new Error('useResponsiveSidebarClose must be used inside <ResponsiveSidebarPanel>');
  }
  return ctx;
};

interface ResponsiveSidebarPanelProps {
  /** Called once the drawer has fully animated out (mobile) or immediately on close (desktop). */
  onClose: () => void;
  /** Accessible title rendered in a visually-hidden DrawerTitle for screen readers. */
  title: string;
  /**
   * Optional accessible description rendered in a visually-hidden DrawerDescription.
   * When omitted, the drawer explicitly opts out via `aria-describedby={undefined}`
   * to suppress Radix's missing-description warning.
   */
  description?: string;
  /** Forwarded to ResizablePanel. Defaults to 2 (right-side sidebar). */
  order?: number;
  /**
   * Mobile only: called instead of closing the drawer, so the parent can check for unsaved
   * changes before it closes.
   */
  onDismiss?: () => void;
  /** Mobile only: `false` keeps a swipe, an overlay click or Escape from closing the drawer. */
  dismissible?: boolean;
  /** Desktop only: a width (percent of the layout) for a panel that needs more room than the default. */
  size?: { id: string; defaultSize: number; minSize: number; maxSize: number };
  children: ReactNode;
}

export const ResponsiveSidebarPanel = ({
  onClose,
  title,
  description,
  order = 2,
  onDismiss,
  dismissible,
  size,
  children,
}: ResponsiveSidebarPanelProps) => {
  const isBelowLg = useBreakpoint('lg');
  const defaultConfig = useSelector(selectSidebarPanelConfig);
  const config = size ?? defaultConfig;

  // Vaul contract: we control `open` locally so the exit animation can play
  // before the Redux-driven parent unmounts this subtree.
  //   mount       → setOpen(true)  → enter animation
  //   user close  → setOpen(false) → exit animation
  //   exit ends   → onClose()      → parent flips Redux → we unmount
  const [isOpen, setIsOpen] = useState(true);

  // Children close via context so both swipe-to-dismiss and in-panel X buttons
  // take the same animated path on mobile. On desktop there is no animation.
  const requestClose = useCallback(() => {
    if (isBelowLg && onDismiss) {
      onDismiss();
    } else if (isBelowLg) {
      setIsOpen(false);
    } else {
      onClose();
    }
  }, [isBelowLg, onClose, onDismiss]);

  if (isBelowLg) {
    return (
      <Drawer
        open={isOpen}
        direction="right"
        dismissible={dismissible}
        autoFocus
        onOpenChange={(next) => {
          if (!next) {
            requestClose();
          }
        }}
        onAnimationEnd={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DrawerContent
          className="w-[90vw] max-w-2xl bg-bg-muted-light"
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          {description ? (
            <DrawerDescription className="sr-only">{description}</DrawerDescription>
          ) : null}
          <div className="h-full overflow-auto">
            <PanelCloseContext.Provider value={requestClose}>{children}</PanelCloseContext.Provider>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <ResizableHandle />
      <ResizablePanel
        id={size?.id}
        className="relative z-50 min-w-80 bg-bg-muted-light"
        order={order}
        defaultSize={config.defaultSize}
        minSize={config.minSize}
        maxSize={config.maxSize}
      >
        <div className="h-full overflow-auto">
          <PanelCloseContext.Provider value={requestClose}>{children}</PanelCloseContext.Provider>
        </div>
      </ResizablePanel>
    </>
  );
};
